import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DataManagement.css';

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const API_URL = process.env.REACT_APP_API_URL || 'https://rytwhv6cal.execute-api.ap-southeast-6.amazonaws.com/dev';

interface DeviceUsage {
  iccid: string;
  imei: string;
  dataUsedMB: number;
  cost: number;
  currency: string;
  startDate: string;
  endDate: string;
  status: string;
  clientId?: string;
  clientName?: string;
}

interface UsageData {
  devices: DeviceUsage[];
  totalUsageMB: number;
  totalCost: number;
  currency: string;
  period: { startDate?: string; endDate?: string };
}

const DataManagement: React.FC = () => {
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);

  const fetchUsageData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await axios.get(`${API_URL}/api/data/usage?${params}`, { headers: getHeaders() });
      setUsageData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch usage data');
      console.error('Usage fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchUsageData();
    }
  }, [startDate, endDate]);

  const filteredDevices = usageData?.devices.filter(device =>
    device.iccid.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.imei.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.clientName?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <div className="data-management">
      <div className="data-header">
        <h1>Data Usage Management</h1>
        <p>Monitor cellular data usage across all devices</p>
      </div>

      <div className="data-filters">
        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by ICCID, IMEI, or Client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button onClick={fetchUsageData} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {usageData && (
        <div className="usage-summary">
          <div className="summary-card">
            <h3>Total Data Used</h3>
            <p className="summary-value">{usageData.totalUsageMB.toFixed(2)} MB</p>
          </div>
          <div className="summary-card">
            <h3>Total Cost</h3>
            <p className="summary-value">{usageData.currency} {usageData.totalCost.toFixed(2)}</p>
          </div>
          <div className="summary-card">
            <h3>Active Devices</h3>
            <p className="summary-value">{usageData.devices.length}</p>
          </div>
        </div>
      )}

      <div className="usage-table-container">
        <table className="usage-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>ICCID</th>
              <th>IMEI</th>
              <th>Data Used (MB)</th>
              <th>Cost</th>
              <th>Status</th>
              <th>Period</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="loading-cell">Loading usage data...</td></tr>
            ) : filteredDevices.length === 0 ? (
              <tr><td colSpan={7} className="empty-cell">No usage data available</td></tr>
            ) : (
              filteredDevices.map((device, index) => (
                <tr key={index}>
                  <td>{device.clientName || 'N/A'}</td>
                  <td className="mono">{device.iccid}</td>
                  <td className="mono">{device.imei}</td>
                  <td className="number">{device.dataUsedMB.toFixed(2)}</td>
                  <td className="number">{device.currency} {device.cost.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge status-${device.status.toLowerCase()}`}>
                      {device.status}
                    </span>
                  </td>
                  <td className="date-range">
                    {device.startDate && device.endDate
                      ? `${new Date(device.startDate).toLocaleDateString()} - ${new Date(device.endDate).toLocaleDateString()}`
                      : 'N/A'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataManagement;
